/*
  MWM colour gallery
  Shows only the product media for the selected colour in the main gallery, the thumbnail
  strip and the zoom modal. Liquid does the first render (snippets/product-media-gallery.liquid,
  product-media-modal.liquid, mwm-media-color.liquid); this script handles colour changes.

  Markup contract:
  - media-gallery[data-mwm-color-position]  1-based position of the product's Color option
      data-mwm-color         colour currently shown
      data-mwm-synced-color  colour of the last server render of the section
  - [data-media-color]  colour the media belongs to, lowercased ('' = every colour)
    [data-media-index]  position of the media in product.media, for stable ordering
  - thumbnails: [data-mwm-label] aria-label template with an [index] placeholder
  Works with Dawn's stacked and thumbnail layouts (desktop) and slider/thumbnail layouts (mobile).

  Rules:
  - Selected colour's media first (variant featured media leads), then media with no colour,
    then other colours hidden.
  - If no media names the selected colour, everything shows (Dawn behaviour).
  - Changing only a non-colour option (size) leaves the gallery as it is.
*/
(() => {
  if (window.mwmColorGallery) return;

  const HIDDEN_CLASS = 'mwm-color-hidden';
  const normalize = (value) => String(value ?? '').trim().toLowerCase();
  const isColorOptionName = (name) => ['color', 'colour'].includes(normalize(name));

  const galleryFor = (productInfo) =>
    productInfo?.querySelector('media-gallery[data-mwm-color-position]') || null;

  const modalContentFor = (productInfo) =>
    document
      .getElementById(`ProductModal-${productInfo?.dataset.section}`)
      ?.querySelector('.product-media-modal__content') || null;

  function setHidden(element, hidden) {
    element.classList.toggle(HIDDEN_CLASS, hidden);
    element.hidden = hidden;
  }

  // Orders and shows/hides the children of `container` matching `selector`.
  // Returns the visible elements in their new order.
  function arrange(container, selector, color, hasMatch, isFeatured) {
    if (!container) return [];
    const items = Array.from(container.querySelectorAll(selector));
    if (!items.length) return [];

    const groupOf = (element) => {
      if (!hasMatch) return 0;
      const mediaColor = element.dataset.mediaColor;
      if (mediaColor === color) return 0;
      if (mediaColor === '') return 1;
      return 2;
    };

    const ordered = items
      .map((element) => {
        const group = groupOf(element);
        return {
          element,
          group,
          featured: group === 0 && isFeatured(element) ? 0 : 1,
          index: Number(element.dataset.mediaIndex) || 0,
        };
      })
      .sort((a, b) => a.group - b.group || a.featured - b.featured || a.index - b.index);

    ordered.forEach(({ element, group }) => setHidden(element, group === 2));

    const orderChanged = ordered.some(({ element }, i) => items[i] !== element);
    if (orderChanged) {
      ordered.forEach(({ element }) => {
        // Moving a node can replay Dawn's reveal animation; cancel it like product-info.js does.
        if (element.classList.contains('scroll-trigger')) element.classList.add('scroll-trigger--cancel');
        container.appendChild(element);
      });
    }

    return ordered.filter(({ group }) => group !== 2).map(({ element }) => element);
  }

  function relabelThumbnails(thumbnails) {
    const counters = new Map();
    thumbnails.forEach((thumbnail) => {
      const template = thumbnail.dataset.mwmLabel;
      const position = (counters.get(template) || 0) + 1;
      counters.set(template, position);
      thumbnail.dataset.mediaPosition = position;
      const button = thumbnail.querySelector('button');
      if (template && button) button.setAttribute('aria-label', template.replace('[index]', position));
    });
  }

  // Dawn sets these thumbnail-strip classes from the media count at render time
  // (snippets/product-media-gallery.liquid); keep them in step with the visible count.
  function syncThumbnailStrip(gallery, count) {
    const strip = gallery.elements?.thumbnails || gallery.querySelector('[id^="GalleryThumbnails"]');
    if (!strip) return;
    strip.classList.toggle('thumbnail-slider--no-slide', count <= 3);
    strip.querySelectorAll(':scope > .slider-button').forEach((button) => {
      button.classList.toggle('small-hide', count <= 3);
      button.classList.toggle('medium-hide', count <= 4);
      button.classList.toggle('large-up-hide', count <= 4);
    });
  }

  function applyColor(productInfo, rawColor, { featuredMediaId = null, reset = true } = {}) {
    const gallery = galleryFor(productInfo);
    if (!gallery) return;

    const color = normalize(rawColor);
    const sectionId = productInfo.dataset.section;
    const modalContent = modalContentFor(productInfo);
    const featuredKey = featuredMediaId ? `${sectionId}-${featuredMediaId}` : null;

    const tagged = [
      ...gallery.querySelectorAll('[data-media-color]'),
      ...(modalContent ? modalContent.querySelectorAll('[data-media-color]') : []),
    ];
    const hasMatch = tagged.some((element) => element.dataset.mediaColor === color);

    const visibleSlides = arrange(
      gallery.querySelector('[id^="Slider-Gallery"]'),
      ':scope > li[data-media-id]',
      color,
      hasMatch,
      (element) => element.dataset.mediaId === featuredKey
    );

    const visibleThumbnails = arrange(
      gallery.querySelector('[id^="Slider-Thumbnails"]'),
      ':scope > li[data-target]',
      color,
      hasMatch,
      (element) => element.dataset.target === featuredKey
    );
    relabelThumbnails(visibleThumbnails);
    syncThumbnailStrip(gallery, visibleThumbnails.length);

    arrange(
      modalContent,
      ':scope > [data-media-color]',
      color,
      hasMatch,
      (element) => featuredMediaId != null && element.dataset.mediaId === String(featuredMediaId)
    );

    gallery.dataset.mwmColor = color;

    // Dawn's sliders count only slides with a width, so hidden slides drop out after a reset.
    const viewer = gallery.elements?.viewer;
    viewer?.resetPages?.();
    if (viewer && visibleSlides.length < 2) {
      viewer.querySelector('.slider-counter--current')?.replaceChildren('1');
      viewer.querySelector('.slider-counter--total')?.replaceChildren(String(visibleSlides.length));
    }
    gallery.elements?.thumbnails?.resetPages?.();

    if (reset && visibleSlides.length && typeof gallery.setActiveMedia === 'function') {
      gallery.setActiveMedia(visibleSlides[0].dataset.mediaId, false);
    }
  }

  // Swap as soon as the shopper picks a colour, before the section re-render returns.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data: { event, target } }) => {
      if (!target || !isColorOptionName(target.dataset.optionName)) return;
      const productInfo = (event?.target || target).closest('product-info');
      const gallery = galleryFor(productInfo);
      if (!gallery) return;
      // A different product (combined listing) re-renders the whole section instead.
      if (target.dataset.productUrl && target.dataset.productUrl !== productInfo.dataset.url) return;

      const color = normalize(target.value);
      if (color === gallery.dataset.mwmColor) return;
      applyColor(productInfo, color, { reset: true });
    });
  }

  // When the section re-render returns, keep Dawn's variant featured-media behaviour on colour
  // changes, then re-apply the colour rules. Skip it for size-only changes so the gallery stays put.
  const ProductInfo = customElements.get('product-info');
  if (ProductInfo && !ProductInfo.prototype.mwmColorGalleryPatched) {
    const originalUpdateMedia = ProductInfo.prototype.updateMedia;

    ProductInfo.prototype.updateMedia = function (html, variantFeaturedMediaId) {
      const gallery = galleryFor(this);
      if (!gallery) return originalUpdateMedia.call(this, html, variantFeaturedMediaId);

      const position = Number(gallery.dataset.mwmColorPosition);
      const variant = this.getSelectedVariant(html);
      const color = normalize(variant?.options?.[position - 1]);
      if (!color) return originalUpdateMedia.call(this, html, variantFeaturedMediaId);

      const shown = gallery.dataset.mwmColor;
      const synced = gallery.dataset.mwmSyncedColor;
      if (color === shown && color === synced) return; // size-only change

      originalUpdateMedia.call(this, html, variantFeaturedMediaId);
      gallery.dataset.mwmSyncedColor = color;
      applyColor(this, color, { featuredMediaId: variantFeaturedMediaId, reset: true });
    };

    ProductInfo.prototype.mwmColorGalleryPatched = true;
  }

  window.mwmColorGallery = { applyColor };
})();
